// =====================================================
// 📁 TextAreaField.tsx
// =====================================================

import React from 'react';

import { IconKey } from '../../../types/system/icons';
import InputIconByKey from './InputIconByKey';
import RequiredAsteriskTooltip from './RequiredAsteriskTooltip';

interface TextAreaFieldProps {
  label?: string;
  name: string;
  value?: string;
  onChange?: (
    event: React.ChangeEvent<HTMLTextAreaElement>
  ) => void;
  onKeyDown?: (
    event: React.KeyboardEvent<HTMLTextAreaElement>
  ) => void;
  onFocus?: (
    event: React.FocusEvent<HTMLTextAreaElement>
  ) => void;
  onBlur?: (
    event: React.FocusEvent<HTMLTextAreaElement>
  ) => void;
  error?: string;
  placeholder?: string;
  rows?: number;
  maxLength?: number;
  disabled?: boolean;
  iconKey?: IconKey;
  required?: boolean;
  helperText?: string;
  rightContent?: React.ReactNode;
  withMargin?: boolean;
  className?: string;
}

const TextAreaField: React.FC<TextAreaFieldProps> = ({
  label,
  name,
  value,
  onChange,
  onKeyDown,
  onFocus,
  onBlur,
  error,
  placeholder = 'Escribe aquí...',
  rows = 4,
  maxLength,
  disabled = false,
  iconKey,
  required = false,
  helperText,
  rightContent,
  withMargin = true,
  className = '',
}) => {
  // =====================================================
  // 🔹 Identificadores de accesibilidad
  // =====================================================

  const errorId = `${name}-error`;
  const helperId = `${name}-helper`;

  const describedBy = error
    ? errorId
    : helperText
      ? helperId
      : undefined;

  // =====================================================
  // 🎨 Render
  // =====================================================

  return (
    <div
      className={`
        w-full
        ${withMargin ? 'mb-5' : ''}
        ${className}
      `}
      data-field={name}
    >
      {label && (
        <label
          htmlFor={name}
          className="
            mb-2.5
            flex
            items-center
            gap-1

            text-sm
            font-medium
            text-black

            dark:text-white
          "
        >
          {label}

          {required && (
            <RequiredAsteriskTooltip delay={400} />
          )}
        </label>
      )}

      <div className="flex flex-wrap items-start gap-2">
        <div
          className={`
            flex
            min-w-[180px]
            flex-1
            overflow-hidden

            rounded-xl
            border

            bg-white

            transition-all
            duration-200

            dark:bg-meta-4

            ${
              error
                ? `
                  border-danger
                  focus-within:border-danger
                  focus-within:ring-2
                  focus-within:ring-danger/10

                  dark:border-danger
                `
                : `
                  border-stroke
                  focus-within:border-primary
                  focus-within:ring-2
                  focus-within:ring-primary/10

                  dark:border-strokedark
                `
            }

            ${
              disabled
                ? `
                  cursor-not-allowed
                  bg-gray-2
                  opacity-70

                  dark:bg-boxdark-2
                `
                : ''
            }
          `}
        >
          {iconKey && (
            <div
              className="
                flex
                items-start
                justify-center

                border-r
                border-stroke

                bg-gray-2

                px-4
                pt-4

                dark:border-strokedark
                dark:bg-boxdark-2
              "
            >
              <InputIconByKey iconKey={iconKey} />
            </div>
          )}

          <textarea
            id={name}
            name={name}
            value={value ?? ''}
            rows={rows}
            maxLength={maxLength}
            disabled={disabled}
            required={required}
            placeholder={placeholder}
            aria-invalid={Boolean(error)}
            aria-describedby={describedBy}
            onChange={onChange}
            onKeyDown={onKeyDown}
            onFocus={onFocus}
            onBlur={onBlur}
            className="
              w-full
              resize-none
              bg-transparent

              px-4
              py-3.5

              text-sm
              text-black

              outline-none

              placeholder:text-body

              disabled:cursor-not-allowed

              dark:text-white
              dark:placeholder:text-bodydark2
            "
          />
        </div>

        {rightContent && (
          <div
            className="
              w-full

              text-right
              text-xs
              font-medium
              text-bodydark2

              sm:w-auto
              sm:min-w-[90px]
              sm:pt-3.5

              dark:text-bodydark
            "
          >
            {rightContent}
          </div>
        )}
      </div>

      {helperText && !error && (
        <p
          id={helperId}
          className="
            mt-1.5
            text-xs
            text-body

            dark:text-bodydark2
          "
        >
          {helperText}
        </p>
      )}

      {error && (
        <p
          id={errorId}
          className="mt-1.5 text-sm text-danger"
        >
          {error}
        </p>
      )}
    </div>
  );
};

export default TextAreaField;